/* ═══════════════════════════════════════════════════════════════
   MODULE UPDATE PRs — the one brain (Phase 6,
   docs/MASTERS_UNIFIED_PLAN.md §"Module update PRs + naming pass").

   Somebody updates a module DEFINITION. Every non-project copy of that
   module — the office masters it was dropped into, and the client
   variants that dropped it into their own layer — gets a PR-style feed
   entry: the definition's block-level ops, read AGAINST that copy as it
   stands today, with accept / reject per op.

   ⛔ STAMPED COPY LAW still holds (v2.12 grave): a landed block is a
   COPY, never a link. An update does not "refresh" anything — it offers
   the change and the reader decides. Rejecting is a knowing deviation
   and leaves the block exactly as it is.

   ── The semantics (locked) ────────────────────────────────────────
     · modified  ACCEPT = take the definition's new words onto this
                          copy's block (its id and its stamp stay)
                 REJECT = leave the block alone
     · added     ACCEPT = drop the new block in beside its neighbours
                 REJECT = don't
     · removed   ACCEPT = delete the block from this copy
                 REJECT = keep it (it stays part of the insertion)
     · unmappable — the block this op names was deleted from this copy.
                 Arrives already settled with a note; never applied.

   When an entry is fully answered, every surviving block of the
   insertions it touched has its stamp moved to the new version — the
   copy has now ANSWERED that version, deviations included, so the next
   update diffs from the right base. Same policy Phase 3 set when it
   re-bases a rejected override so it stops reading as stale.

   This file re-exports `variant-updates.ts` so the API server can load
   BOTH halves of the feed from one bundle
   (`npm run build:server-specs` → ec2-api/shared-variant-updates.js).
   Never hand-mirror either half.
   ═══════════════════════════════════════════════════════════════ */

import type { ClientLayer, ContentBlock, LayerAdd } from './spec';
import { normalizeHeading } from './heading-normalize';
import { CONTENT_KEYS, hashBlock } from './spec-compose';
import type { ModuleBlock, ModuleDefDiff, ModuleStamp, SpecModule } from './spec-modules';
import {
  DEVIATED_HASH,
  lcsPairs,
  mapDefOpsToInstance,
  restampInsertion,
  stampOf,
} from './spec-modules';
import type { IdContext, VariantChange, VariantChangeEntry } from './variant-updates';
import {
  buildVariantChangeEntries,
  decideAll,
  decideChange,
  excerptOf,
  idOf,
  isEntryResolved,
  nowOf,
  refFor,
} from './variant-updates';

export * from './variant-updates';

/* SYNC (docs/SYNC_PLAN.md §2.2): the server raises Review rows into project
   copies with the SAME engine and the SAME fork the client uses — ride this
   bundle, never hand-mirror. */
export * as sync from './sync';
export { forkMasterBlocks } from './spec-fork';
export { diffModuleDefinition } from './spec-modules';

/* ─── Shapes ─────────────────────────────────────────────────── */

/** One copy of the module that an update has to be offered to. */
export interface ModuleUpdateTarget {
  specMasterId: string;
  /** The `ClientLayer.id` — or NULL for the office master's own core
   *  content. There is deliberately no third kind: a project spec is never
   *  a target (hard wall). */
  layerId: string | null;
  clientId?: string;
  /** This copy's blocks in document order: the master's `coreContent`, or
   *  the blocks a client layer ADDED (`layerAddBlocks`). */
  blocks: ContentBlock[];
}

export interface BuildModuleUpdateArgs {
  moduleId: string;
  moduleName: string;
  fromVersion: number;
  toVersion: number;
  diff: ModuleDefDiff;
  targets: ModuleUpdateTarget[];
  createdBy?: string;
  ctx?: IdContext;
}

/** The blocks a client layer added — the layer's own copy of anything
 *  inserted while a variant was on screen. */
export const layerAddBlocks = (layer: Pick<ClientLayer, 'adds'>): ContentBlock[] =>
  (layer?.adds || []).map((a) => a.block).filter(Boolean);

/* ─── Generation ─────────────────────────────────────────────── */

/** Replace a block's CONTENT with another's, keeping its identity: same id,
 *  same module stamp, same per-spec bookkeeping. The key list is the compose
 *  brain's, so "content" means the same thing here as it does everywhere. */
function withContentOf(target: ContentBlock, source: ContentBlock): ContentBlock {
  const out = { ...(target as unknown as Record<string, unknown>) };
  const src = source as unknown as Record<string, unknown>;
  for (const key of CONTENT_KEYS) {
    if (src[key] === undefined) delete out[key];
    else out[key] = src[key];
  }
  return out as unknown as ContentBlock;
}

/** A definition block on its way into a copy: fresh id (minted at RAISE time
 *  so the client and the server insert the very same block), stamped at the
 *  version this update carries. */
function newBlockFor(defBlock: ContentBlock, id: string, stamp: ModuleStamp): ContentBlock {
  const copy = JSON.parse(JSON.stringify(defBlock)) as ContentBlock & {
    sourceBlockId?: string;
  };
  copy.id = id;
  copy.moduleStamp = { ...stamp };
  // A definition may carry its OWN grouping (modules keep groups since
  // modules-polish item 1). That id means nothing in this document, so a
  // paragraph an update adds joins no group here — exactly as before groups
  // were kept. Grouping is structure, never content: it is not what an
  // update is about.
  delete copy.groupId;
  return copy as ContentBlock;
}

/**
 * The ops one COPY sees, already answered against what that copy holds
 * today. Empty ⇒ this copy has nothing to review.
 */
export function buildModuleUpdateChanges(args: {
  moduleId: string;
  moduleName: string;
  toVersion: number;
  diff: ModuleDefDiff;
  blocks: ContentBlock[];
  ctx?: IdContext;
}): VariantChange[] {
  const { moduleId, moduleName, toVersion, diff, blocks, ctx } = args;
  const byId = new Map((blocks || []).map((b) => [b.id, b]));
  const indexById = new Map((blocks || []).map((b, i) => [b.id, i]));
  // A chunk that merged under the spec's own heading never had the
  // definition's heading block — no op on THAT chunk's heading slot may
  // surface as an offer. Tracked per `insertId:chunkIndex`.
  const chunkOfSource = new Map<string, number>();
  (diff.oldChunks || []).forEach((chunkBlocks, ci) =>
    chunkBlocks.forEach((b) => chunkOfSource.set(b.id, ci))
  );
  const mergedSlots = new Set<string>();
  for (const b of blocks || []) {
    const st = stampOf(b);
    if (!st || st.moduleId !== moduleId || !st.mergedHeading || !st.sourceBlockId) continue;
    const ci = chunkOfSource.get(st.sourceBlockId);
    if (ci !== undefined) mergedSlots.add(`${st.insertId}:${ci}`);
  }
  const mapped = mapDefOpsToInstance(diff, blocks || [], moduleId).filter((m) => {
    if (!mergedSlots.has(`${m.insertId}:${m.op.chunkIndex}`)) return true;
    const side = m.op.after || m.op.before;
    const isHeadingSlot = m.op.ordinal === 0 && !!side && (side.level || 1) === 2;
    if (!isHeadingSlot) return true;
    if (m.unmappable) return false;
    if (m.op.kind !== 'added') return true;
    // Only the merged heading itself is suppressed — a genuinely different
    // heading the definition adds at ordinal 0 is still offered.
    const merged = (diff.oldChunks?.[m.op.chunkIndex]?.[0]?.text as string) || '';
    return normalizeHeading(m.op.after?.text || '') !== normalizeHeading(merged);
  });

  // Two ADDs in a row have to chain: the second goes after the first, which
  // does not exist in the document yet — so it anchors on the id we just
  // minted for it.
  const lastAddId = new Map<string, string>();
  const out: VariantChange[] = [];

  for (const m of mapped) {
    const op = m.op;
    const kind = op.kind === 'modified' ? 'modified' : op.kind === 'added' ? 'added' : 'removed';
    const settledNote =
      op.kind === 'added'
        ? 'Nothing to attach to — the blocks around this one were removed from this copy.'
        : 'Nothing to apply — this block was removed from this copy.';

    if (m.unmappable) {
      out.push({
        changeId: idOf(ctx, 'chg'),
        blockId: op.before?.id || op.after?.id || '',
        kind,
        ...(op.before ? { beforeBlock: op.before } : {}),
        ...(op.after ? { afterBlock: op.after } : {}),
        ref: '',
        excerpt: excerptOf(op.after || op.before),
        hadOverride: false,
        hadHide: false,
        insertId: m.insertId,
        unmappable: true,
        decision: 'accepted',
        decidedAt: nowOf(ctx),
        note: settledNote,
      });
      continue;
    }

    if (op.kind === 'added') {
      const chainKey = `${m.insertId}:${op.chunkIndex}`;
      const chained = lastAddId.get(chainKey);
      const anchorAfterId = chained || m.afterBlockId;
      const anchorBeforeId = chained ? undefined : m.beforeBlockId;
      const newId = idOf(ctx, 'mb');
      lastAddId.set(chainKey, newId);
      const anchorIndex = indexById.get(anchorAfterId || anchorBeforeId || '') ?? 0;
      out.push({
        changeId: idOf(ctx, 'chg'),
        blockId: newId,
        kind: 'added',
        afterBlock: newBlockFor(op.after as ContentBlock, newId, {
          moduleId,
          moduleName,
          moduleVersion: toVersion,
          insertId: m.insertId,
        }),
        ref: refFor(blocks || [], anchorIndex),
        excerpt: excerptOf(op.after),
        hadOverride: false,
        hadHide: false,
        insertId: m.insertId,
        ...(anchorAfterId ? { anchorAfterId } : {}),
        ...(anchorBeforeId ? { anchorBeforeId } : {}),
        decision: 'pending',
      });
      continue;
    }

    const instance = byId.get(m.instanceBlockId || '');
    if (!instance) continue;
    out.push({
      changeId: idOf(ctx, 'chg'),
      blockId: instance.id,
      kind,
      // ⛔ BEFORE is what THIS copy has on screen, never what the old
      // definition said — the reader compares against their own document.
      beforeBlock: instance,
      ...(op.kind === 'modified' && op.after
        ? { afterBlock: withContentOf(instance, op.after) }
        : {}),
      ref: refFor(blocks || [], indexById.get(instance.id) ?? 0),
      excerpt: excerptOf(op.after || instance),
      // "This copy has its own wording here" — the block drifted from the
      // definition it came from, so accepting overwrites a local edit.
      hadOverride: !!op.before && hashBlock(instance) !== hashBlock(op.before),
      hadHide: false,
      insertId: m.insertId,
      decision: 'pending',
    });
  }

  return out;
}

/**
 * One definition update → one feed row per copy it can reach. A target the
 * update cannot touch produces nothing at all.
 */
export function buildModuleUpdateEntries(args: BuildModuleUpdateArgs): VariantChangeEntry[] {
  const { moduleId, moduleName, fromVersion, toVersion, diff, targets, createdBy, ctx } = args;
  const entries: VariantChangeEntry[] = [];
  for (const target of targets || []) {
    const changes = buildModuleUpdateChanges({
      moduleId,
      moduleName,
      toVersion,
      diff,
      blocks: target.blocks || [],
      ctx,
    });
    if (!changes.length) continue;
    entries.push({
      id: idOf(ctx, 'vcu'),
      specMasterId: target.specMasterId,
      layerId: target.layerId,
      ...(target.clientId ? { clientId: target.clientId } : {}),
      fromVersion,
      toVersion,
      createdAt: nowOf(ctx),
      ...(createdBy ? { createdBy } : {}),
      changes,
      status: isEntryResolved({ changes }) ? 'resolved' : 'pending',
      moduleId,
      moduleName,
      moduleFromVersion: fromVersion,
      moduleToVersion: toVersion,
      defDiff: diff,
    });
  }
  return entries;
}

/* ─── Decisions ──────────────────────────────────────────────── */

const versionOf = (entry: Pick<VariantChangeEntry, 'moduleToVersion' | 'toVersion'>): number =>
  entry.moduleToVersion ?? entry.toVersion ?? 1;

/** Move the stamp of every surviving block of these insertions to the version
 *  just answered. The copy has now SEEN that version — an accepted block
 *  because it matches it, a rejected one because its deviation is deliberate. */
export function bumpInsertionStamps(
  blocks: ContentBlock[],
  moduleId: string,
  insertIds: Set<string>,
  toVersion: number
): ContentBlock[] {
  if (!insertIds.size) return blocks;
  return (blocks || []).map((block) => {
    const stamp = stampOf(block);
    if (!stamp || stamp.moduleId !== moduleId || !insertIds.has(stamp.insertId)) return block;
    if (stamp.moduleVersion === toVersion) return block;
    return { ...block, moduleStamp: { ...stamp, moduleVersion: toVersion } };
  });
}

const insertIdsOf = (entry: Pick<VariantChangeEntry, 'changes'>): Set<string> =>
  new Set((entry.changes || []).map((c) => c.insertId).filter((x): x is string => !!x));

/**
 * Turn ONE decision into the blocks it means. PURE — hand it the copy's
 * blocks, get new blocks back; the caller persists them.
 */
export function applyModuleDecisionToBlocks(
  blocks: ContentBlock[],
  change: VariantChange,
  decision: 'accepted' | 'rejected',
  toVersion: number
): ContentBlock[] {
  const list = blocks || [];
  // Nothing to apply, and REJECT never touches the document at all: the copy
  // keeps exactly what it has (a knowing deviation).
  if (change.unmappable || decision === 'rejected') return list;

  if (change.kind === 'removed') {
    return list.filter((b) => b.id !== change.blockId);
  }

  if (change.kind === 'modified') {
    const next = change.afterBlock;
    if (!next) return list;
    return list.map((b) =>
      b.id !== change.blockId
        ? b
        : {
            ...withContentOf(b, next),
            ...(stampOf(b)
              ? { moduleStamp: { ...(stampOf(b) as ModuleStamp), moduleVersion: toVersion } }
              : {}),
          }
    );
  }

  // added
  return insertAtAnchor(list, change.afterBlock, change);
}

/** An accepted ADD, spliced in beside the neighbours the change names. Shared
 *  by the raised feed and the local one (R2 Phase 3) so a new paragraph can
 *  never land in two different places depending on who asked. */
export function insertAtAnchor(
  blocks: ContentBlock[],
  block: ContentBlock | undefined,
  change: Pick<VariantChange, 'anchorAfterId' | 'anchorBeforeId' | 'insertId'>
): ContentBlock[] {
  const list = blocks || [];
  if (!block || list.some((b) => b.id === block.id)) return list;
  let insertAt = -1;
  if (change.anchorAfterId) {
    const at = list.findIndex((b) => b.id === change.anchorAfterId);
    if (at >= 0) insertAt = at + 1;
  }
  if (insertAt < 0 && change.anchorBeforeId) {
    const at = list.findIndex((b) => b.id === change.anchorBeforeId);
    if (at >= 0) insertAt = at;
  }
  if (insertAt < 0 && change.insertId) {
    // The anchor is not there: it was deleted between the raise and the
    // decision, or it was an earlier ADD in this chain that got rejected. The
    // end of THIS insertion is the honest spot — never the end of the
    // document, which would strand the block in someone else's Part.
    for (let i = list.length - 1; i >= 0; i--) {
      if (stampOf(list[i])?.insertId === change.insertId) {
        insertAt = i + 1;
        break;
      }
    }
  }
  if (insertAt < 0) insertAt = list.length;
  return [...list.slice(0, insertAt), block, ...list.slice(insertAt)];
}

/** Everything one decision changes: the feed row AND the copy's blocks. */
export function applyModuleDecision(
  entry: VariantChangeEntry,
  blocks: ContentBlock[],
  changeId: string,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; blocks: ContentBlock[] } {
  const change = (entry.changes || []).find((c) => c.changeId === changeId);
  if (!change || change.decision !== 'pending') return { entry, blocks: blocks || [] };
  const toVersion = versionOf(entry);
  const nextEntry = decideChange(entry, changeId, decision, ctx);
  let next = applyModuleDecisionToBlocks(blocks || [], change, decision, toVersion);
  if (nextEntry.status === 'resolved') {
    next = bumpInsertionStamps(next, entry.moduleId || '', insertIdsOf(entry), toVersion);
  }
  return { entry: nextEntry, blocks: next };
}

/** Accept all / Reject all — every pending op in one entry, in order. */
export function applyModuleDecisionAll(
  entry: VariantChangeEntry,
  blocks: ContentBlock[],
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; blocks: ContentBlock[] } {
  const toVersion = versionOf(entry);
  let next = blocks || [];
  for (const change of entry.changes || []) {
    if (change.decision !== 'pending') continue;
    next = applyModuleDecisionToBlocks(next, change, decision, toVersion);
  }
  const nextEntry = decideAll(entry, decision, ctx);
  if (nextEntry.status === 'resolved') {
    next = bumpInsertionStamps(next, entry.moduleId || '', insertIdsOf(entry), toVersion);
  }
  return { entry: nextEntry, blocks: next };
}

/* ─── The layer flavour of the same decision ─────────────────── */

/** The layer's adds, rewritten by one decision. Adds carry their own
 *  placement (`afterBlockId`), so a new block is spliced in beside the add it
 *  belongs next to, never appended blindly. */
export function applyModuleDecisionToAdds(
  adds: LayerAdd[],
  change: VariantChange,
  decision: 'accepted' | 'rejected',
  toVersion: number
): LayerAdd[] {
  const list = adds || [];
  if (change.unmappable || decision === 'rejected') return list;

  if (change.kind === 'removed') {
    return list.filter((a) => a.block?.id !== change.blockId);
  }

  if (change.kind === 'modified') {
    const next = change.afterBlock;
    if (!next) return list;
    return list.map((a) => {
      if (a.block?.id !== change.blockId) return a;
      const stamp = stampOf(a.block);
      return {
        ...a,
        block: {
          ...withContentOf(a.block, next),
          ...(stamp ? { moduleStamp: { ...stamp, moduleVersion: toVersion } } : {}),
        },
      };
    });
  }

  const block = change.afterBlock;
  if (!block || list.some((a) => a.block?.id === block.id)) return list;
  let insertAt = -1;
  if (change.anchorAfterId) {
    const at = list.findIndex((a) => a.block?.id === change.anchorAfterId);
    if (at >= 0) insertAt = at + 1;
  }
  if (insertAt < 0 && change.anchorBeforeId) {
    const at = list.findIndex((a) => a.block?.id === change.anchorBeforeId);
    if (at >= 0) insertAt = at;
  }
  if (insertAt < 0 && change.insertId) {
    for (let i = list.length - 1; i >= 0; i--) {
      const stamp = list[i].block ? stampOf(list[i].block) : null;
      if (stamp?.insertId === change.insertId) {
        insertAt = i + 1;
        break;
      }
    }
  }
  if (insertAt < 0) insertAt = list.length;
  const add: LayerAdd = {
    block,
    ...(change.anchorAfterId ? { afterBlockId: change.anchorAfterId } : {}),
  };
  return [...list.slice(0, insertAt), add, ...list.slice(insertAt)];
}

function bumpAddStamps(
  adds: LayerAdd[],
  moduleId: string,
  insertIds: Set<string>,
  toVersion: number
): LayerAdd[] {
  if (!insertIds.size) return adds;
  return (adds || []).map((a) => {
    const stamp = a.block ? stampOf(a.block) : null;
    if (!stamp || stamp.moduleId !== moduleId || !insertIds.has(stamp.insertId)) return a;
    if (stamp.moduleVersion === toVersion) return a;
    return { ...a, block: { ...a.block, moduleStamp: { ...stamp, moduleVersion: toVersion } } };
  });
}

/** One decision on a CLIENT layer's copy. Never touches core content — the
 *  same wall Phase 3 built: a variant's decision rewrites the variant only. */
export function applyModuleDecisionToLayer(
  layer: ClientLayer,
  entry: VariantChangeEntry,
  changeId: string,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; layer: ClientLayer } {
  const change = (entry.changes || []).find((c) => c.changeId === changeId);
  if (!change || change.decision !== 'pending') return { entry, layer };
  const toVersion = versionOf(entry);
  const nextEntry = decideChange(entry, changeId, decision, ctx);
  let adds = applyModuleDecisionToAdds(layer.adds || [], change, decision, toVersion);
  if (nextEntry.status === 'resolved') {
    adds = bumpAddStamps(adds, entry.moduleId || '', insertIdsOf(entry), toVersion);
  }
  return { entry: nextEntry, layer: { ...layer, adds } };
}

/* ─── The cascade: a master accept IS a publish ──────────────────
   (Josh's ruling 2026-08-26, verbatim: "if it's a module placed in a
   master, the PR goes to the master, which PRs to the variants.")

   A module update answered on an OFFICE MASTER rewrites that master's core
   content outside the draft flow — so the one thing a publish always does
   never happened: no client version was ever asked. A variant with its own
   wording on a touched block would silently keep it with no record, and a
   variant without one would silently inherit. Both are decisions somebody
   has to make, which is precisely what the Phase 3 feed is for.

   So the accept cascades: diff the master's core from BEFORE the answer to
   AFTER it, through the SAME `buildVariantChangeEntries` a publish calls —
   same override/hide annotations, same auto-accept on hidden blocks, same
   ordinary entries the existing panel already draws. Nothing about a module
   travels down: the variant is being told the MASTER moved, which is all it
   has ever needed to know.

   ⛔ Two guards, both here so the server cannot drift from the tests:
     · `entryTookContent` — a rejected op changes no words, so it cascades
       nothing. It is deliberately the SAME predicate that moves the
       master's version: one answer → one version → one cascade.
     · that predicate only fires on a RESOLVED entry, which is what stops
       the double-raise when an entry's ops are answered one at a time.
       Every accept mutates the core, but only the LAST one closes the
       entry, and it diffs against the pre-answer core the version-history
       snapshot preserved — so the cascade describes the whole answer once,
       never once per op.
─────────────────────────────────────────────────────────────── */

/** Did answering this entry actually TAKE anything into the copy? Only then
 *  did the document move, and only then is there anything to pass on.
 *  `unmappable` ops arrive pre-accepted and apply nothing, so they never
 *  count. */
export function entryTookContent(entry: Pick<VariantChangeEntry, 'status' | 'changes'>): boolean {
  return (
    entry.status === 'resolved' &&
    (entry.changes || []).some((c) => c.decision === 'accepted' && !c.unmappable)
  );
}

/**
 * One answered master-target module update → the variant entries a publish
 * of the same words would have raised. Empty when the master carries no
 * client versions, or when the answer moved no words (reject-only, or an
 * accept that only re-stamped blocks — a stamp is not content, so the diff
 * is empty and nothing is raised).
 */
export function buildMasterAcceptCascadeEntries(args: {
  specMasterId: string;
  /** The master's version BEFORE the answer, and after it — the pair the
   *  panel prints on the card. */
  fromVersion: number;
  toVersion: number;
  /** Core content as it stood before the first accept on this entry. */
  prevCore: ContentBlock[];
  /** Core content now that the entry is fully answered. */
  nextCore: ContentBlock[];
  layers: ClientLayer[];
  /** Whoever answered the module update — this feed is their doing. */
  createdBy?: string;
  ctx?: IdContext;
}): VariantChangeEntry[] {
  const { specMasterId, fromVersion, toVersion, prevCore, nextCore, layers, createdBy, ctx } = args;
  if (!layers || !layers.length) return [];
  return buildVariantChangeEntries({
    specMasterId,
    fromVersion,
    toVersion,
    prevCore: prevCore || [],
    nextCore: nextCore || [],
    layers,
    ...(createdBy ? { createdBy } : {}),
    ...(ctx ? { ctx } : {}),
  });
}

/* ═══════════════════════════════════════════════════════════════
   THE LOCAL UPDATE — "this document is behind the library" (R2 Phase 3,
   docs/MODULES_R2_PLAN.md).

   The raised feed above answers "the definition MOVED, here is the diff of
   the move". This half answers the other question, the one a spec asks
   when it opens: "the library says v3 and my copy says v1 — what is
   different?" No old definition is needed, because the comparison is the
   one the reader actually cares about:

       the CURRENT library definition  vs  what THIS copy says today

   matched paragraph for paragraph on `sourceBlockId` (the stamp's
   traceback, Phase 1). Everything downstream is the shipped feed: the same
   `VariantChange` cards, the same accept/reject words, the same panel.

   ── The resolution rule (locked, R2 Phase 3) ──────────────────────
     · ACCEPT a paragraph → this copy takes the library's words, and that
       block is re-stamped at the new version with a fresh traceback and a
       fresh content hash: it reads ✓ intact again.
     · REJECT a paragraph → nothing is written. A knowing deviation.
     · Once EVERY offer is answered:
         – at least one accept → the whole insertion moves to the new
           version (rejected blocks keep their words, so the module reads
           "● modified here" AT the new version — it has answered v3)
         – nothing accepted ("Keep all mine") → the version does NOT move;
           `dismissedVersion` records what was declined, so the badge goes
           quiet and the NEXT version offers again on its own.
   ═══════════════════════════════════════════════════════════════ */

/** A local update is DERIVED, so it is rebuilt from the document every time
 *  it is looked at — which means its ids have to be the same every time or a
 *  decision would not survive the next keystroke. */
const localChangeId = (insertId: string, toVersion: number, slot: string): string =>
  `chg-lmu-${insertId}-${toVersion}-${slot}`;

/** The id a block ADDED by the library update will land under. Deterministic
 *  for the same reason, and it becomes that block's real id on accept. */
const localAddId = (insertId: string, slot: string): string => `mb-lmu-${insertId}-${slot}`;

export const localEntryId = (insertId: string, toVersion: number): string =>
  `lmu-${insertId}-${toVersion}`;

/**
 * Pair up the slots the traceback could not: LCS over content hashes finds the
 * paragraphs that are still word-for-word identical (the anchors), and
 * whatever sits between two anchors pairs off in order — so an edited
 * paragraph answers for its slot and only a genuinely added or dropped one is
 * left over. Mutates `pairAt` / `matched`, which is what the caller reads.
 */
function alignOpenSlots(
  def: ModuleBlock[],
  local: ContentBlock[],
  pairAt: Map<number, ContentBlock>,
  matched: Set<string>
): void {
  const openDef = def.map((d, i) => ({ d, i })).filter(({ i }) => !pairAt.has(i));
  const openLocal = local.filter((b) => !matched.has(b.id));
  if (!openDef.length || !openLocal.length) return;

  const anchors = lcsPairs(
    openDef.map(({ d }) => hashBlock(d)),
    openLocal.map((b) => hashBlock(b))
  );

  let di = 0;
  let li = 0;
  const take = (d: number, l: number): void => {
    pairAt.set(openDef[d].i, openLocal[l]);
    matched.add(openLocal[l].id);
  };
  const fillGap = (dEnd: number, lEnd: number): void => {
    while (di < dEnd && li < lEnd) {
      take(di, li);
      di++;
      li++;
    }
    di = dEnd;
    li = lEnd;
  };
  for (const [d, l] of anchors) {
    fillGap(d, l);
    take(d, l);
    di = d + 1;
    li = l + 1;
  }
  fillGap(openDef.length, openLocal.length);
}

export interface LocalModuleUpdateArgs {
  /** The library definition as it stands NOW. */
  module: Pick<SpecModule, 'id' | 'name' | 'version' | 'chunks'>;
  /** The open document's blocks, in document order. */
  blocks: ContentBlock[];
  /** The one insertion this update is about. */
  insertId: string;
  specMasterId?: string;
  layerId?: string | null;
}

/**
 * One insertion, read against the current library definition. Null when there
 * is nothing to offer: no such insertion, the copy is already at (or past) the
 * library version, or the words are identical anyway.
 */
export function buildLocalModuleUpdate(args: LocalModuleUpdateArgs): VariantChangeEntry | null {
  const { module, insertId } = args;
  const all = args.blocks || [];
  const local = all.filter((b) => stampOf(b)?.insertId === insertId);
  if (!local.length) return null;

  const stamps = local.map((b) => stampOf(b) as ModuleStamp);
  if (stamps.some((s) => s.moduleId !== module.id)) return null;

  const fromVersion = stamps.reduce(
    (v, s) => Math.min(v, s.moduleVersion ?? 1),
    Number.POSITIVE_INFINITY
  );
  const toVersion = module.version ?? 1;
  if (!Number.isFinite(fromVersion) || toVersion <= fromVersion) return null;

  const def = (module.chunks || []).flatMap((c) => c?.blocks || []);
  const defIds = new Set(def.map((d) => d.id).filter(Boolean));
  // Every block knowing where it came from is what makes a per-paragraph
  // merge honest. One that does not drops the WHOLE insertion back to
  // take-it-or-leave-it, matched by position (R2 Phase 3, old inserts).
  const traceable = stamps.every((s) => !!s.sourceBlockId);

  const indexById = new Map(all.map((b, i) => [b.id, i]));
  const pairAt = new Map<number, ContentBlock>();
  const matched = new Set<string>();

  // 1. THE TRACEBACK. A block that still knows which definition block it came
  //    from names its slot exactly, whatever has happened to the words.
  if (traceable) {
    const bySource = new Map<string, ContentBlock>();
    for (const block of local) {
      const src = stampOf(block)?.sourceBlockId;
      // FIRST wins: a block the user duplicated inside the insertion carries
      // the same traceback, and the copy is theirs, not the module's.
      if (src && !bySource.has(src)) bySource.set(src, block);
    }
    def.forEach((d, i) => {
      const hit = d.id ? bySource.get(d.id) : undefined;
      if (!hit || matched.has(hit.id)) return;
      pairAt.set(i, hit);
      matched.add(hit.id);
    });
  }

  // 2. WHAT THE TRACEBACK CANNOT NAME. A definition RE-CAPTURED from a live
  //    selection mints fresh ids for every block (the identity ruling in
  //    spec-modules.ts), so v2 may share no id with the version this copy
  //    landed from. The leftovers are aligned on content — the very same
  //    anchors-then-gaps alignment the raised feed maps its ops with, so a
  //    locally-edited paragraph still pairs with its slot and only a genuinely
  //    new or dropped one is left over.
  alignOpenSlots(def, local, pairAt, matched);

  const changes: VariantChange[] = [];
  const slotOf = (i: number): string => (traceable && def[i]?.id ? def[i].id : `p${i}`);

  /** The block a new paragraph goes after: the nearest earlier slot that
   *  exists in this copy, or the one this update just minted for it. */
  let prevAnchorId: string | undefined;

  def.forEach((defBlock, i) => {
    const mine = pairAt.get(i);
    if (mine) {
      prevAnchorId = mine.id;
      if (hashBlock(mine) === hashBlock(defBlock)) return;
      const stamp = stampOf(mine) as ModuleStamp;
      const after = withContentOf(mine, defBlock as ContentBlock);
      changes.push({
        changeId: localChangeId(insertId, toVersion, slotOf(i)),
        blockId: mine.id,
        kind: 'modified',
        // ⛔ BEFORE is what this copy says today — never the old definition.
        beforeBlock: mine,
        afterBlock: {
          ...after,
          moduleStamp: {
            ...stamp,
            moduleVersion: toVersion,
            ...(defBlock.id ? { sourceBlockId: defBlock.id } : {}),
            contentHash: hashBlock(defBlock),
          },
        },
        ref: refFor(all, indexById.get(mine.id) ?? 0),
        excerpt: excerptOf(defBlock),
        // The words here are this spec's own — accepting overwrites an edit
        // somebody made on purpose.
        hadOverride: !!stamp.contentHash && hashBlock(mine) !== stamp.contentHash,
        hadHide: false,
        insertId,
        decision: 'pending',
      });
      return;
    }

    // A paragraph only the NEW version has: offered as an insertion.
    let anchorBeforeId: string | undefined;
    if (!prevAnchorId) {
      for (let j = i + 1; j < def.length && !anchorBeforeId; j++) {
        anchorBeforeId = pairAt.get(j)?.id;
      }
    }
    const newId = localAddId(insertId, slotOf(i));
    const block = newBlockFor(defBlock as ContentBlock, newId, {
      moduleId: module.id,
      moduleName: module.name,
      moduleVersion: toVersion,
      insertId,
      ...(defBlock.id ? { sourceBlockId: defBlock.id } : {}),
      contentHash: hashBlock(defBlock),
    });
    changes.push({
      changeId: localChangeId(insertId, toVersion, slotOf(i)),
      blockId: newId,
      kind: 'added',
      afterBlock: block,
      ref: refFor(all, indexById.get(prevAnchorId || anchorBeforeId || '') ?? 0),
      excerpt: excerptOf(defBlock),
      hadOverride: false,
      hadHide: false,
      insertId,
      ...(prevAnchorId ? { anchorAfterId: prevAnchorId } : {}),
      ...(!prevAnchorId && anchorBeforeId ? { anchorBeforeId } : {}),
      decision: 'pending',
    });
    // Two new paragraphs in a row chain: the second goes after the first,
    // which does not exist in the document yet.
    prevAnchorId = newId;
  });

  // A paragraph the new version no longer carries: offered as a removal.
  // Only when the definition has NOTHING by that traceback — a block whose
  // slot is still there but was matched by an earlier copy of itself is the
  // user's own duplicate and is never touched.
  for (const block of local) {
    if (matched.has(block.id)) continue;
    const src = stampOf(block)?.sourceBlockId;
    if (traceable && src && defIds.has(src)) continue;
    changes.push({
      changeId: localChangeId(insertId, toVersion, `gone-${block.id}`),
      blockId: block.id,
      kind: 'removed',
      beforeBlock: block,
      ref: refFor(all, indexById.get(block.id) ?? 0),
      excerpt: excerptOf(block),
      hadOverride: false,
      hadHide: false,
      insertId,
      decision: 'pending',
    });
  }

  if (!changes.length) return null;

  return {
    id: localEntryId(insertId, toVersion),
    specMasterId: args.specMasterId || '',
    layerId: args.layerId ?? null,
    fromVersion,
    toVersion,
    changes,
    status: 'pending',
    moduleId: module.id,
    moduleName: module.name,
    moduleFromVersion: fromVersion,
    moduleToVersion: toVersion,
    local: true,
    insertId,
    ...(traceable ? {} : { wholeModuleOnly: true }),
  };
}

/** Every insertion in a document that has something to review, newest
 *  library version first in the document's own order. */
export function buildLocalModuleUpdates(args: {
  modules: Array<Pick<SpecModule, 'id' | 'name' | 'version' | 'chunks'>>;
  blocks: ContentBlock[];
  insertIds: string[];
  specMasterId?: string;
  layerId?: string | null;
}): VariantChangeEntry[] {
  const byId = new Map(args.modules.map((m) => [m.id, m]));
  const out: VariantChangeEntry[] = [];
  for (const insertId of args.insertIds || []) {
    const stamp = (args.blocks || [])
      .map((b) => stampOf(b))
      .find((s) => s && s.insertId === insertId);
    const module = stamp ? byId.get(stamp.moduleId) : undefined;
    if (!module) continue;
    const entry = buildLocalModuleUpdate({
      module,
      blocks: args.blocks,
      insertId,
      ...(args.specMasterId ? { specMasterId: args.specMasterId } : {}),
      ...(args.layerId !== undefined ? { layerId: args.layerId } : {}),
    });
    if (entry) out.push(entry);
  }
  return out;
}

/**
 * Sync D7: one review per (copy, module). A derived local update for an
 * insertion that a RAISED module entry (same module, same copy) already
 * offers is the same question twice — the raised one wins, the local one is
 * dropped. Pure.
 */
export function localUpdatesNotRaised(
  local: VariantChangeEntry[],
  raised: VariantChangeEntry[]
): VariantChangeEntry[] {
  const covered = new Set<string>();
  for (const e of raised || []) {
    if (!e || e.local || e.status !== 'pending' || !e.moduleId) continue;
    for (const c of e.changes || []) {
      if (c.insertId) covered.add(`${e.moduleId}|${e.layerId ?? ''}|${c.insertId}`);
    }
  }
  if (!covered.size) return local;
  return (local || []).filter(
    (e) => !covered.has(`${e.moduleId}|${e.layerId ?? ''}|${e.insertId}`)
  );
}

/**
 * ONE answer on a local update → the blocks it means. Pure.
 * REJECT writes nothing at all; ACCEPT takes the block the change already
 * carries (stamp included, so an accepted paragraph re-stamps itself).
 */
export function applyLocalModuleDecisionToBlocks(
  blocks: ContentBlock[],
  change: VariantChange,
  decision: 'accepted' | 'rejected'
): ContentBlock[] {
  const list = blocks || [];
  if (decision === 'rejected') return list;
  if (change.kind === 'removed') return list.filter((b) => b.id !== change.blockId);
  if (change.kind === 'modified') {
    const next = change.afterBlock;
    if (!next) return list;
    return list.map((b) => (b.id === change.blockId ? next : b));
  }
  return insertAtAnchor(list, change.afterBlock, change);
}

/**
 * The close-out, once every offer in a local update has been answered.
 * Took something ⇒ the copy is AT the new version (deviations included, the
 * same policy the raised feed uses). Took nothing ⇒ the version stays where
 * it is and the decline is recorded, so a later version offers again.
 */
export function settleLocalModuleUpdate(
  blocks: ContentBlock[],
  entry: Pick<VariantChangeEntry, 'changes' | 'insertId' | 'moduleToVersion' | 'toVersion'>
): { blocks: ContentBlock[]; settled: boolean; tookContent: boolean } {
  const list = blocks || [];
  const changes = entry.changes || [];
  const insertId = entry.insertId || changes.find((c) => c.insertId)?.insertId || '';
  const toVersion = entry.moduleToVersion ?? entry.toVersion ?? 1;
  if (!insertId || !changes.length || changes.some((c) => c.decision === 'pending')) {
    return { blocks: list, settled: false, tookContent: false };
  }
  const tookContent = changes.some((c) => c.decision === 'accepted');
  if (!tookContent) {
    return {
      blocks: restampInsertion(list, insertId, { dismissedVersion: toVersion }),
      settled: true,
      tookContent: false,
    };
  }
  const deviations = localDeviationPatches(changes);
  return {
    blocks: (list || []).map((block) => {
      const stamp = stampOf(block);
      if (!stamp || stamp.insertId !== insertId) return block;
      return {
        ...block,
        moduleStamp: {
          ...stamp,
          moduleVersion: toVersion,
          ...(deviations[block.id] || {}),
        },
      };
    }),
    settled: true,
    tookContent: true,
  };
}

/**
 * The stamp patches a REJECTED offer leaves behind, per block: the module's
 * own words at the version just answered, so the copy reads honestly as
 * "● modified here" instead of pretending it still matches. A rejected
 * REMOVAL keeps a paragraph the module no longer has at all — nothing to
 * hash, so the deviation is recorded as such.
 */
export function localDeviationPatches(
  changes: VariantChange[]
): Record<string, Partial<ModuleStamp>> {
  const out: Record<string, Partial<ModuleStamp>> = {};
  for (const change of changes || []) {
    if (change.decision !== 'rejected') continue;
    if (change.kind === 'modified' && change.afterBlock) {
      out[change.blockId] = { contentHash: hashBlock(change.afterBlock) };
    } else if (change.kind === 'removed') {
      out[change.blockId] = { contentHash: DEVIATED_HASH };
    }
  }
  return out;
}

/** Accept all / Reject all ("Keep all mine") on a local update, blocks and
 *  entry together — the shape the panel's bulk buttons answer with. */
export function applyLocalModuleDecisionAll(
  entry: VariantChangeEntry,
  blocks: ContentBlock[],
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; blocks: ContentBlock[] } {
  let next = blocks || [];
  for (const change of entry.changes || []) {
    if (change.decision !== 'pending') continue;
    next = applyLocalModuleDecisionToBlocks(next, change, decision);
  }
  const nextEntry = decideAll(entry, decision, ctx);
  return { entry: nextEntry, blocks: settleLocalModuleUpdate(next, nextEntry).blocks };
}

/** One answer on a local update, blocks and entry together. */
export function applyLocalModuleDecision(
  entry: VariantChangeEntry,
  blocks: ContentBlock[],
  changeId: string,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; blocks: ContentBlock[] } {
  const change = (entry.changes || []).find((c) => c.changeId === changeId);
  if (!change || change.decision !== 'pending') return { entry, blocks: blocks || [] };
  const nextEntry = decideChange(entry, changeId, decision, ctx);
  const next = applyLocalModuleDecisionToBlocks(blocks || [], change, decision);
  return { entry: nextEntry, blocks: settleLocalModuleUpdate(next, nextEntry).blocks };
}

/** Accept all / Reject all on a client layer's copy. */
export function applyModuleDecisionAllToLayer(
  layer: ClientLayer,
  entry: VariantChangeEntry,
  decision: 'accepted' | 'rejected',
  ctx?: IdContext
): { entry: VariantChangeEntry; layer: ClientLayer } {
  const toVersion = versionOf(entry);
  let adds = layer.adds || [];
  for (const change of entry.changes || []) {
    if (change.decision !== 'pending') continue;
    adds = applyModuleDecisionToAdds(adds, change, decision, toVersion);
  }
  const nextEntry = decideAll(entry, decision, ctx);
  if (nextEntry.status === 'resolved') {
    adds = bumpAddStamps(adds, entry.moduleId || '', insertIdsOf(entry), toVersion);
  }
  return { entry: nextEntry, layer: { ...layer, adds } };
}
